import React, { useState, useEffect } from 'react';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
} from '@material-ui/core';
import { useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import useDebounce from '@forvalter/utils/useDebounce';
import { getLocalizedLabel } from '../../utils';
import type { FormField } from '../../interfaces';

interface EnumSelectProps {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
    enumValues: Array<string>;
    defaultValue?: string;
    value?: string;
}

export const EnumSelect: React.FC<EnumSelectProps> = ({
    field,
    isReadOnly,
    isRequired,
    enumValues,
    defaultValue,
    value,
}) => {
    const { t } = useTranslation();
    const { id, label } = field;
    const { values, setFieldValue, getFieldMeta, handleBlur } =
        useFormikContext<Record<string, unknown>>();
    const [formikField] = useField(id);

    const fieldValue = (values as Record<string, unknown>)[id as string];
    const meta = getFieldMeta(id as string);

    // Local state for immediate UI updates
    const [localValue, setLocalValue] = useState(fieldValue);

    // Use debounced value for Formik updates
    const { debouncedValue } = useDebounce(localValue, 300);

    // Update local state when field value changes from outside
    useEffect(() => {
        setLocalValue(fieldValue);
    }, [fieldValue]);

    // Update Formik when debounced value changes
    useEffect(() => {
        if (debouncedValue !== fieldValue) {
            setFieldValue(id as string, debouncedValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedValue]);

    const handleChange = (event: React.ChangeEvent<{ value: unknown }>) => {
        setLocalValue(event.target.value);
    };

    const handleFieldBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        // Immediately sync on blur to ensure validation
        setFieldValue(id as string, localValue);
        handleBlur(event);
    };

    return (
        <FormControl fullWidth={true} required={isRequired}>
            <InputLabel id={`${id}-label`}>
                {t(`project.formFields.${id}`)}
            </InputLabel>
            <Select
                {...formikField}
                variant="outlined"
                name={id}
                value={localValue ?? ''}
                onChange={handleChange}
                onBlur={handleFieldBlur}
                labelId={`${id}-label`}
                id={id}
                defaultValue={value ?? defaultValue}
                label={getLocalizedLabel(`project.formFields.${id}`, label, id)}
                required={isRequired}
                disabled={isReadOnly}
                error={meta.touched && Boolean(meta.error)}
            >
                {enumValues.map((enumValue) => (
                    <MenuItem key={enumValue} value={enumValue}>
                        {enumValue}
                    </MenuItem>
                ))}
            </Select>
            {meta.touched && meta.error && (
                <FormHelperText error={true}>{meta.error}</FormHelperText>
            )}
        </FormControl>
    );
};
